import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Loader2,
  Edit,
  X,
  Handshake,
  Plus,
  Copy,
  TrendingUp,
  Users,
  DollarSign,
} from 'lucide-react';
import { apiRequest } from '../lib/api';

interface PartnerCode {
  id: string;
  code: string;
  short_code: string | null;
  user_id: string;
  is_partner_code: boolean;
  partner_name: string | null;
  partner_notes: string | null;
  custom_l1_subscription_rate: number | null;
  custom_l1_trading_rate: number | null;
  expires_at: string | null;
  attribution_window_days: number;
  is_active: boolean;
  click_count: number;
  created_at: string;
  // Performance stats
  stats?: {
    total_referrals: number;
    converted_referrals: number;
    total_earnings: number;
    earnings_this_month: number;
  };
}

interface PartnerFormData {
  code: string;
  user_id: string;
  partner_name: string;
  partner_notes: string;
  custom_l1_subscription_rate: string;
  custom_l1_trading_rate: string;
  attribution_window_days: string;
  expires_at: string;
  is_active: boolean;
}

interface EditModalProps {
  partner: PartnerCode | null;
  onClose: () => void;
  onSave: (data: any) => void;
  isLoading: boolean;
}

function EditPartnerModal({ partner, onClose, onSave, isLoading }: EditModalProps) {
  const [formData, setFormData] = useState<PartnerFormData>({
    code: partner?.code || '',
    user_id: partner?.user_id || '',
    partner_name: partner?.partner_name || '',
    partner_notes: partner?.partner_notes || '',
    custom_l1_subscription_rate: partner?.custom_l1_subscription_rate
      ? (partner.custom_l1_subscription_rate * 100).toString()
      : '10',
    custom_l1_trading_rate: partner?.custom_l1_trading_rate
      ? (partner.custom_l1_trading_rate * 100).toString()
      : '10',
    attribution_window_days: partner?.attribution_window_days?.toString() || '30',
    expires_at: partner?.expires_at
      ? new Date(partner.expires_at).toISOString().split('T')[0]
      : '',
    is_active: partner?.is_active ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      code: formData.code.toUpperCase().replace(/[^A-Z0-9]/g, ''),
      user_id: formData.user_id.toLowerCase(),
      partner_name: formData.partner_name || null,
      partner_notes: formData.partner_notes || null,
      custom_l1_subscription_rate: parseFloat(formData.custom_l1_subscription_rate) / 100,
      custom_l1_trading_rate: parseFloat(formData.custom_l1_trading_rate) / 100,
      attribution_window_days: parseInt(formData.attribution_window_days),
      expires_at: formData.expires_at || null,
      is_active: formData.is_active,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-white/10 w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-sm font-medium text-white">
            {partner ? 'Edit Partner Code' : 'Create Partner Code'}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">
                Vanity Code <span className="text-emerald-500">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({
                  ...formData,
                  code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
                })}
                placeholder="e.g., WHALE"
                maxLength={15}
                className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                  placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500 uppercase"
                required
                disabled={!!partner}
              />
              <p className="text-xs text-zinc-600 mt-1">3-15 alphanumeric characters</p>
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">
                Partner Name
              </label>
              <input
                type="text"
                value={formData.partner_name}
                onChange={(e) => setFormData({ ...formData, partner_name: e.target.value })}
                placeholder="e.g., Crypto Whale"
                className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                  placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-zinc-500 mb-1">
              Wallet Address <span className="text-emerald-500">*</span>
            </label>
            <input
              type="text"
              value={formData.user_id}
              onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
              placeholder="0x..."
              className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white font-mono
                placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
              required
              disabled={!!partner}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">
                L1 Subscription Rate
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="10"
                  max="50"
                  step="1"
                  value={formData.custom_l1_subscription_rate}
                  onChange={(e) => setFormData({ ...formData, custom_l1_subscription_rate: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                    placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500 pr-8"
                  required
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">%</span>
              </div>
              <p className="text-xs text-zinc-600 mt-1">First month subscription</p>
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">
                L1 Trading Fee Rate
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="10"
                  max="50"
                  step="1"
                  value={formData.custom_l1_trading_rate}
                  onChange={(e) => setFormData({ ...formData, custom_l1_trading_rate: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                    placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500 pr-8"
                  required
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">%</span>
              </div>
              <p className="text-xs text-zinc-600 mt-1">Ongoing trading fees</p>
            </div>
          </div>

          <div className="p-3 bg-white/5 border border-white/10">
            <p className="text-xs text-zinc-400">
              <span className="text-zinc-300">Note:</span> L2 is fixed at 2%, L3 at 1% for all partners.
              Subscription renewals are always 10% for everyone.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">
                Attribution Window
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="7"
                  max="90"
                  value={formData.attribution_window_days}
                  onChange={(e) => setFormData({ ...formData, attribution_window_days: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                    placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500 pr-14"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">days</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">
                Expiry Date
              </label>
              <input
                type="date"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                  placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500
                  [color-scheme:dark]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-zinc-500 mb-1">
              Internal Notes
            </label>
            <textarea
              value={formData.partner_notes}
              onChange={(e) => setFormData({ ...formData, partner_notes: e.target.value })}
              placeholder="Admin-only notes about this partner..."
              rows={2}
              className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500 resize-none"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 bg-black border border-white/10"
            />
            <label htmlFor="is_active" className="text-xs text-zinc-400">
              Partner code is active
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm text-zinc-400 border border-white/10 hover:bg-white/5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white
                disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface PerformanceModalProps {
  partner: PartnerCode;
  onClose: () => void;
}

function PerformanceModal({ partner, onClose }: PerformanceModalProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-partner-performance', partner.id],
    queryFn: () => apiRequest('GET', `/api/admin/partner-codes/${partner.id}/performance`),
  });

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-white/10 w-full max-w-2xl">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h2 className="text-sm font-medium text-white">
              Partner Performance: {partner.partner_name || partner.code}
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">Code: {partner.code}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
          </div>
        ) : (
          <div className="p-4 space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 text-zinc-500 mb-2">
                  <Users className="w-4 h-4" />
                  <span className="text-xs">Total Referrals</span>
                </div>
                <p className="text-2xl font-medium text-white">{data?.stats?.total_referrals || 0}</p>
              </div>
              <div className="p-4 bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 text-zinc-500 mb-2">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs">Converted</span>
                </div>
                <p className="text-2xl font-medium text-emerald-400">{data?.stats?.converted || 0}</p>
              </div>
              <div className="p-4 bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 text-zinc-500 mb-2">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-xs">Total Earnings</span>
                </div>
                <p className="text-2xl font-medium text-white">
                  ${(data?.stats?.total_earnings || 0).toFixed(2)}
                </p>
              </div>
              <div className="p-4 bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 text-zinc-500 mb-2">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-xs">This Month</span>
                </div>
                <p className="text-2xl font-medium text-emerald-400">
                  ${(data?.stats?.earnings_this_month || 0).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Earnings Breakdown */}
            <div>
              <h3 className="text-xs text-zinc-500 mb-3">Earnings Breakdown</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-white/5 border border-white/10">
                  <p className="text-xs text-zinc-500">From Trading Fees</p>
                  <p className="text-lg font-medium text-white mt-1">
                    ${(data?.stats?.earnings_from_trading || 0).toFixed(2)}
                  </p>
                </div>
                <div className="p-3 bg-white/5 border border-white/10">
                  <p className="text-xs text-zinc-500">From Subscriptions</p>
                  <p className="text-lg font-medium text-white mt-1">
                    ${(data?.stats?.earnings_from_subscriptions || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Recent Referrals */}
            <div>
              <h3 className="text-xs text-zinc-500 mb-3">Recent Referrals</h3>
              {data?.recent_referrals?.length > 0 ? (
                <div className="border border-white/10">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-white/5">
                        <th className="text-left text-xs text-zinc-500 px-3 py-2">User</th>
                        <th className="text-left text-xs text-zinc-500 px-3 py-2">Level</th>
                        <th className="text-left text-xs text-zinc-500 px-3 py-2">Status</th>
                        <th className="text-left text-xs text-zinc-500 px-3 py-2">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_referrals.map((ref: any) => (
                        <tr key={ref.id} className="border-t border-white/5">
                          <td className="px-3 py-2 text-xs font-mono text-zinc-300">
                            {ref.referred_id.slice(0, 6)}...{ref.referred_id.slice(-4)}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-xs px-1.5 py-0.5 ${
                              ref.level === 1 ? 'bg-emerald-500/20 text-emerald-400' :
                              ref.level === 2 ? 'bg-blue-500/20 text-blue-400' :
                              'bg-purple-500/20 text-purple-400'
                            }`}>
                              L{ref.level}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-xs px-1.5 py-0.5 ${
                              ref.status === 'converted'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-amber-500/20 text-amber-400'
                            }`}>
                              {ref.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-zinc-500">
                            {new Date(ref.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-zinc-500">No referrals yet</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Partners() {
  const queryClient = useQueryClient();
  const [editingPartner, setEditingPartner] = useState<PartnerCode | null>(null);
  const [viewingPartner, setViewingPartner] = useState<PartnerCode | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-partner-codes'],
    queryFn: () => apiRequest('GET', '/api/admin/partner-codes'),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => apiRequest('POST', '/api/admin/partner-codes', data),
    onSuccess: () => {
      toast.success('Partner code created successfully');
      setIsCreating(false);
      queryClient.invalidateQueries({ queryKey: ['admin-partner-codes'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create partner code');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      apiRequest('PUT', `/api/admin/partner-codes/${id}`, data),
    onSuccess: () => {
      toast.success('Partner code updated successfully');
      setEditingPartner(null);
      queryClient.invalidateQueries({ queryKey: ['admin-partner-codes'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update partner code');
    },
  });

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(`https://proteus.gg?ref=${code}`);
    toast.success('Referral link copied to clipboard');
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-medium text-white">Partner Codes</h1>
          <p className="text-sm text-zinc-500">Manage custom referral codes for influencers and partners</p>
        </div>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Plus className="w-3 h-3" />
          Add Partner
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-white/5 border border-white/10">
          <p className="text-xs text-zinc-500">Active Partners</p>
          <p className="text-2xl font-medium text-white mt-1">
            {data?.codes?.filter((c: PartnerCode) => c.is_active).length || 0}
          </p>
        </div>
        <div className="p-4 bg-white/5 border border-white/10">
          <p className="text-xs text-zinc-500">Total Clicks</p>
          <p className="text-2xl font-medium text-white mt-1">
            {data?.codes?.reduce((sum: number, c: PartnerCode) => sum + (c.click_count || 0), 0) || 0}
          </p>
        </div>
        <div className="p-4 bg-white/5 border border-white/10">
          <p className="text-xs text-zinc-500">Total Referrals</p>
          <p className="text-2xl font-medium text-white mt-1">
            {data?.summary?.total_referrals || 0}
          </p>
        </div>
        <div className="p-4 bg-white/5 border border-white/10">
          <p className="text-xs text-zinc-500">Total Earnings Paid</p>
          <p className="text-2xl font-medium text-emerald-400 mt-1">
            ${(data?.summary?.total_earnings || 0).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
        </div>
      ) : (
        <div className="border border-white/10 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-white/5">
                <th className="text-left text-xs text-zinc-500 px-4 py-3">Partner</th>
                <th className="text-left text-xs text-zinc-500 px-4 py-3">Code</th>
                <th className="text-left text-xs text-zinc-500 px-4 py-3">L1 Sub</th>
                <th className="text-left text-xs text-zinc-500 px-4 py-3">L1 Trading</th>
                <th className="text-left text-xs text-zinc-500 px-4 py-3">Clicks</th>
                <th className="text-left text-xs text-zinc-500 px-4 py-3">Status</th>
                <th className="text-right text-xs text-zinc-500 px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data?.codes?.map((partner: PartnerCode) => (
                <tr key={partner.id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                        <Handshake className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-white">{partner.partner_name || 'Unnamed Partner'}</p>
                        <p className="text-xs text-zinc-500 font-mono">
                          {partner.user_id.slice(0, 6)}...{partner.user_id.slice(-4)}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-emerald-400">{partner.code}</span>
                      <button
                        onClick={() => copyCode(partner.code)}
                        className="p-1 text-zinc-500 hover:text-white"
                        title="Copy referral link"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-mono text-blue-400">
                      {((partner.custom_l1_subscription_rate || 0.10) * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-mono text-blue-400">
                      {((partner.custom_l1_trading_rate || 0.10) * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-300">
                    {partner.click_count?.toLocaleString() || 0}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 ${
                      partner.is_active
                        ? partner.expires_at && new Date(partner.expires_at) < new Date()
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-zinc-500/20 text-zinc-400'
                    }`}>
                      {partner.is_active
                        ? partner.expires_at && new Date(partner.expires_at) < new Date()
                          ? 'Expired'
                          : 'Active'
                        : 'Inactive'
                      }
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setViewingPartner(partner)}
                        className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/10"
                        title="View Performance"
                      >
                        <TrendingUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingPartner(partner)}
                        className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/10"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(!data?.codes || data.codes.length === 0) && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-zinc-500 text-sm">
                    No partner codes yet. Create your first partner code to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {isCreating && (
        <EditPartnerModal
          partner={null}
          onClose={() => setIsCreating(false)}
          onSave={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
        />
      )}

      {/* Edit Modal */}
      {editingPartner && (
        <EditPartnerModal
          partner={editingPartner}
          onClose={() => setEditingPartner(null)}
          onSave={(data) => updateMutation.mutate({ id: editingPartner.id, data })}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Performance Modal */}
      {viewingPartner && (
        <PerformanceModal
          partner={viewingPartner}
          onClose={() => setViewingPartner(null)}
        />
      )}
    </div>
  );
}
